// Error Detection Types and Utilities
import { AlertTriangle, FileCode, Wrench, ChevronDown, ChevronUp, X } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { PxMark } from '@/components/PxMark';

// Structured error information
export interface ErrorInfo {
    id: string;
    type: 'build' | 'runtime' | 'white_page';
    message: string;
    shortMessage: string; // Concise version for display
    files: string[]; // Affected files
    suggestions: string[];
    timestamp: Date;
    rawError?: string; // Full error for AI context
}

// Parse raw error string into structured ErrorInfo
export function parseError(rawError: string): ErrorInfo {
    const id = `error-${Date.now()}`;

    // Detect error type
    let type: ErrorInfo['type'] = 'build';
    let shortMessage = '';
    const files: string[] = [];
    const suggestions: string[] = [];

    // Extract file references
    const fileMatches = rawError.matchAll(/(?:from\s+)?["']([^"']+\.tsx?)["']/gi);
    for (const match of fileMatches) {
        if (!files.includes(match[1])) {
            files.push(match[1]);
        }
    }

    // Detect "Failed to resolve import" errors
    const importMatch = rawError.match(/Failed to resolve import ["']([^"']+)["'] from ["']([^"']+)["']/i);
    if (importMatch) {
        shortMessage = `Import manquant : "${importMatch[1]}" dans ${importMatch[2]}`;
        suggestions.push(`Générer le fichier ${importMatch[1]}`);
        suggestions.push(`Vérifier le chemin d'import`);
    }

    // Detect "does not provide an export named" errors
    const exportMatch = rawError.match(/does not provide an export named ["']([^"']+)["']/i);
    if (exportMatch) {
        shortMessage = `Export manquant : "${exportMatch[1]}"`;
        suggestions.push(`Ajouter l'export ${exportMatch[1]} au fichier source`);
    }

    // Detect syntax errors
    if (rawError.includes('SyntaxError') || rawError.includes('Unexpected token')) {
        shortMessage = 'Erreur de syntaxe détectée';
        type = 'build';
        suggestions.push('Vérifier la syntaxe TypeScript/JSX');
    }

    // Detect TypeScript errors
    const tsMatch = rawError.match(/error TS(\d+):\s*(.+)/i);
    if (tsMatch) {
        shortMessage = `TypeScript TS${tsMatch[1]}: ${tsMatch[2].slice(0, 60)}...`;
        suggestions.push('Corriger l\'erreur TypeScript');
    }

    // Detect runtime errors
    if (rawError.includes('ReferenceError') || rawError.includes('TypeError')) {
        type = 'runtime';
        shortMessage = rawError.match(/(Reference|Type)Error:\s*(.+)/)?.[0]?.slice(0, 80) || 'Erreur runtime';
    }

    // Fallback short message
    if (!shortMessage) {
        const firstLine = rawError.split('\n')[0];
        shortMessage = firstLine.length > 80 ? firstLine.slice(0, 80) + '...' : firstLine;
    }

    return {
        id,
        type,
        message: rawError,
        shortMessage,
        files,
        suggestions,
        timestamp: new Date(),
        rawError
    };
}

// Error Banner Component
interface ErrorBannerProps {
    error: ErrorInfo;
    onTryFix: () => void;
    onDismiss: () => void;
    isFixing: boolean;
}

export function ErrorBanner({ error, onTryFix, onDismiss, isFixing }: ErrorBannerProps) {
    const [isExpanded, setIsExpanded] = useState(false);

    // Deux tons sémantiques seulement : err (build/runtime), warn (page blanche)
    const typeLabels = {
        build: { label: 'Erreur de build', text: 'text-err', badge: 'bg-err/10 text-err', edge: 'border-l-err' },
        runtime: { label: 'Erreur runtime', text: 'text-err', badge: 'bg-err/10 text-err', edge: 'border-l-err' },
        white_page: { label: 'Page blanche', text: 'text-warn', badge: 'bg-warn/10 text-warn', edge: 'border-l-warn' },
    };

    const style = typeLabels[error.type];

    return (
        <div className={cn(
            "overflow-hidden bg-card border border-border border-l-2 transition-colors",
            style.edge
        )}>
            {/* Header */}
            <div className="flex items-start gap-3 p-3">
                <AlertTriangle className={cn("w-4 h-4 mt-0.5 shrink-0", style.text)} />

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className={cn("font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5", style.badge)}>
                            {style.label}
                        </span>
                        <span className="font-mono text-[10px] text-faint">
                            {error.timestamp.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </div>

                    <p className="text-sm font-medium text-foreground leading-relaxed">
                        {error.shortMessage}
                    </p>

                    {/* Affected files */}
                    {error.files.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            {error.files.slice(0, 3).map((file, i) => (
                                <span key={i} className="inline-flex items-center gap-1 font-mono text-[10px] px-1.5 py-0.5 bg-secondary border border-border text-muted-foreground">
                                    <FileCode className="w-3 h-3" />
                                    {file.split('/').pop()}
                                </span>
                            ))}
                            {error.files.length > 3 && (
                                <span className="font-mono text-[10px] text-faint">
                                    +{error.files.length - 3} fichiers
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Dismiss button */}
                <button
                    onClick={onDismiss}
                    title="Fermer"
                    className="p-1 hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            {/* Expandable details */}
            <div className="border-t border-border">
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="w-full flex items-center justify-between px-3 py-2 font-mono text-[11px] text-muted-foreground hover:bg-secondary transition-colors"
                >
                    <span>Détails de l'erreur</span>
                    {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>

                {isExpanded && (
                    <div className="px-3 pb-3">
                        <pre className="font-mono text-[10px] text-muted-foreground bg-surface-3 p-2 overflow-x-auto max-h-32 overflow-y-auto">
                            {error.rawError}
                        </pre>

                        {/* Suggestions */}
                        {error.suggestions.length > 0 && (
                            <div className="mt-2 space-y-1">
                                <p className="font-mono text-[10px] uppercase tracking-wider text-faint">Suggestions</p>
                                <ul className="space-y-0.5">
                                    {error.suggestions.map((s, i) => (
                                        <li key={i} className="font-mono text-[10px] text-muted-foreground flex items-center gap-1.5">
                                            <span className="text-faint">–</span> {s}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 p-3 border-t border-border">
                <button
                    onClick={onTryFix}
                    disabled={isFixing}
                    className={cn(
                        "flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium transition-colors",
                        "bg-card border border-border text-foreground hover:bg-secondary",
                        "disabled:opacity-40 disabled:cursor-not-allowed"
                    )}
                >
                    {isFixing ? (
                        <>
                            <PxMark state="thinking" size={12} label="Analyse en cours" />
                            <span>Analyse en cours…</span>
                        </>
                    ) : (
                        <>
                            <Wrench className="w-3.5 h-3.5" />
                            <span>Essayer de réparer</span>
                        </>
                    )}
                </button>

                <button
                    onClick={onDismiss}
                    className="px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:underline underline-offset-[3px] transition-colors"
                >
                    Ignorer
                </button>
            </div>
        </div>
    );
}

export default ErrorBanner;
